import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { SINGLE_AD_TYPE } from '../constants';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import { DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE } from '../types/hypothesis';
import {
  collectAdSettingsFlowsToValidate,
  countPackCreatives,
  toAdSetFlowMatch,
} from '../utils/flowAdSetCount';
import {
  validateAdSettingsFieldsAtIndex,
  validateAdSettingsForFlowsAtIndex,
} from './adSettingsValidation';
import { isPartnershipIdFilled, validatePartnershipIdAtIndex } from './partnershipAdSettingsValidation';
import { selectedAdSetsFlowsSchema, validateAdSetsStep } from './validateSelectedAdSetsFlows';

const testCreativesViewStateSchema = z.object({
  search: z.string().default(''),
  hypothesis: z.array(z.string()).default([]),
  customer: z.string().nullable().default(null),
  flow: z.array(z.string()).default([]),
  technology: z.array(z.string()).default([]),
  tpcFinishDate: z.string().nullable().default(null),
  status: z.enum(['launched', 'not_launched']).nullable().default(null),
  uploaded: z
    .enum(['last_week', 'last_2_weeks', '1_month', 'all'])
    .default('last_week'),
  groupByHypothesis: z.boolean().default(true),
  unpackedOrderByHypothesis: z.record(z.array(z.string())).default({}),
});

const schema = z
  .object({
    currentStep: z.number().default(0).optional(),
    app: z.string().nonempty('Application is required'),
    platform: z.string().optional(),
    marketingProcess: z.enum(['inbox', 'testing', 'scaling', 'scaling_eg', 'hypothesis_testing'], {
      errorMap: () => ({ message: 'Marketing process is required' }),
    }),
    adType: z.string().optional(),
    adAccounts: z.array(z.string()).optional(),
    selectedCampaigns: z.array(z.any()).optional(),
    selectedCampaignsOnly: z.boolean(),
    selectedAdSets: z.array(z.any()).optional(),
    selectedCampaignsBudgets: z.record(
      z.string(),
      z.string()
        .refine((val) => val.trim() !== '', {
          message: 'Cannot be empty',
        }),
    ),
    selectedAdSetsBudgets: z.record(z.string(), z.string()).default({}),
    selectedAdSetsDuplicatesCount: z.record(z.string(), z.string()).default({}),
    selectedAdSetsStartTimes: z.record(z.string(), z.string()).default({}),
    selectedAdSetsFlows: selectedAdSetsFlowsSchema,
    selectedCreatives: z.array(z.string()).default([]),
    callToActions: z.string().optional(),
    selectedConversionDomain: z.string().optional(),
    packAdSettings: z.array(z.record(z.unknown())).default([{}]),
    packAdSettingsLocalization: z.record(z.string()).default({}),
    partnershipAdSettingsFields: z.array(z.record(z.unknown())).default([{}]),
    partnershipAdSettingsLocalization: z.array(z.record(z.string())).default([]),
    hotjar: z.boolean().optional(),
    conversionDomain: z.string().optional(),
    adSettings: z.string().optional(),
    adLabelSuffix: z.string().optional(),
    partnershipAdsEnabled: z.boolean().default(false),
    partnershipIsPartnerIdentityFirst: z.boolean().optional(),
    partnershipIdentityToShow: z.nativeEnum(PartnershipIdentityToShow).optional(),
    partnershipCreationMode: z.nativeEnum(PartnershipCreationMode).optional(),
    partnershipAdSettings: z.array(
      z.object({
        partnershipId: z.string().default(''),
        partnershipTag: z.string().default(''),
        partnershipPartnerPageId: z.string().default(''),
        partnershipAppPageId: z.string().default(''),
        partnershipPartnerInstagramId: z.string().nullable().default(null),
      }),
    ).default([]),
    packsWithCreatives: z
      .array(
        z.object({
          tag: z.string().default(''),
          adType: z.string().default(''),
          creatives: z.array(z.string()),
          filterCreativeByName: z.array(z.string()).default([]),
          visual_touch_ups: z.boolean().default(true),
          isMixedPack: z.boolean().optional(),
        }),
      )
      .default([{ tag: '', adType: '', creatives: [], visual_touch_ups: true }]),
    activePacksWithCreatives: z.number().nullable().optional(),
    activePackTag: z.string().default(''),
    filterCampaignsByName: z.array(z.string()).default([]),
    filterAdSetsByName: z.array(z.string()).default([]),
    filterCreativesByName: z.array(z.string()).default([]),
    selectedCreativesOnly: z.boolean().default(false),
    testCreativesViewState: testCreativesViewStateSchema.default(
      DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE,
    ),
  })
  .superRefine((data, ctx) => {
    const currentStep = data.currentStep;

    if (currentStep === 0 && !data?.app) {
      ctx.addIssue({
        code: 'custom',
        message: 'Application is required',
        path: ['app'],
      });
    }

    if (
      currentStep === 0 &&
      !data?.selectedCreatives?.length &&
      data.marketingProcess === 'inbox'
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one creative has to be applied',
        path: ['selectedCreatives'],
      });
    }

    if (currentStep === 0 && data.marketingProcess === 'hypothesis_testing') {
      if (!data.packsWithCreatives.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'Add at least one pack to continue',
          path: ['packsWithCreatives'],
        });
      } else {
        const isSomePackEmpty = data.packsWithCreatives.findIndex(
          (pack) => !pack.creatives.length,
        );

        if (isSomePackEmpty !== -1) {
          ctx.addIssue({
            code: 'custom',
            message: 'Select creatives to continue',
            path: [`packsWithCreatives.${isSomePackEmpty}`, 'creatives'],
          });
        }

        const packWithoutTag = data.packsWithCreatives.findIndex(
          (pack) => !pack.tag.trim(),
        );
        if (packWithoutTag !== -1) {
          ctx.addIssue({
            code: 'custom',
            message: "Can't be empty",
            path: [`packsWithCreatives.${packWithoutTag}.tag`],
          });
        }
      }
    }

    if (
      currentStep === 0 &&
      (data.marketingProcess === 'scaling' || data.marketingProcess === 'testing')
    ) {
      const packWithoutAdType = data.packsWithCreatives.findIndex(
        (pack) => !pack.adType?.trim(),
      );

      if (packWithoutAdType !== -1) {
        ctx.addIssue({
          code: 'custom',
          message: "Can't be empty",
          path: ['packsWithCreatives', packWithoutAdType, 'adType'],
        });
      }

      const isSomePackEmpty = data.packsWithCreatives.findIndex(
        (pack) => !pack.creatives.length,
      );

      if (isSomePackEmpty !== -1) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select creatives to continue',
          path: [`packsWithCreatives.${isSomePackEmpty}`, 'creatives'],
        });
      }
    }

    if (currentStep === 1 && !data?.adAccounts?.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'AdAccounts is required at this step',
        path: ['adAccounts'],
      });
    }

    if (currentStep === 1 && !data?.platform) {
      ctx.addIssue({
        code: 'custom',
        message: 'Platform is required',
        path: ['platform'],
      });
    }

    if (currentStep === 2 && !data?.selectedCampaigns?.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one campaign must be selected at this step',
        path: ['selectedCampaigns'],
      });
    }
    if (currentStep === 3) {
      validateAdSetsStep(data, ctx, {
        validateBudgets: true,
        validateDuplicates: true,
        duplicatesMax: 5,
      });
    }

    if ((currentStep ?? 0) >= 4 && !data?.callToActions) {
      ctx.addIssue({
        code: 'custom',
        message: 'Can\'t be empty',
        path: ['callToActions'],
      });
    }

    if (
      (currentStep ?? 0) >= 4 &&
      data.platform === 'WEB' &&
      !data?.selectedConversionDomain?.length
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Can\'t be empty',
        path: ['selectedConversionDomain'],
      });
    }

    const allPacksAreSingleAd = data.packsWithCreatives.every(
      (pack) => pack.adType === SINGLE_AD_TYPE,
    );
    const isPartnershipAdsFlow = data.partnershipAdsEnabled && allPacksAreSingleAd;

    if ((currentStep ?? 0) >= 4 && data.partnershipAdsEnabled && !allPacksAreSingleAd) {
      ctx.addIssue({
        code: 'custom',
        message: 'Partnership ads are only available for Single Ad type',
        path: ['partnershipAdsEnabled'],
      });
    }

    if ((currentStep ?? 0) >= 4 && !data.partnershipAdsEnabled) {
      const localizationIssueMessages = Object.values(data.packAdSettingsLocalization ?? {});
      if (localizationIssueMessages.length > 0) {
        ctx.addIssue({
          code: 'custom',
          message: localizationIssueMessages[0] ?? 'Missing localized text for selected creatives',
          path: ['packAdSettingsLocalization'],
        });
      }

      const selectedAdSetsForFlowMatch = (data.selectedAdSets ?? []).map(toAdSetFlowMatch);

      data.packsWithCreatives.forEach((pack, index) => {
        const flowsToValidate = collectAdSettingsFlowsToValidate(
          data.packAdSettings[index],
          selectedAdSetsForFlowMatch,
          countPackCreatives(pack),
        );

        validateAdSettingsForFlowsAtIndex(
          data.packAdSettings[index],
          index,
          ctx,
          'packAdSettings',
          flowsToValidate,
        );
      });
    }

    if ((currentStep ?? 0) >= 4 && isPartnershipAdsFlow) {
      if (!data.partnershipAdSettings.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'Add at least one partnership',
          path: ['partnershipAdSettings'],
        });
      }

      data.partnershipAdSettings.forEach((setting, index) => {
        validatePartnershipIdAtIndex(setting.partnershipId, index, ctx);
      });

      const partnershipLocalizationMessages = data.partnershipAdSettings.flatMap(
        (_, index) => Object.values(data.partnershipAdSettingsLocalization[index] ?? {}),
      );
      if (partnershipLocalizationMessages.length > 0) {
        ctx.addIssue({
          code: 'custom',
          message:
            partnershipLocalizationMessages[0] ??
            'Missing localized text for selected creatives',
          path: ['partnershipAdSettingsLocalization'],
        });
      }

      if (!data.partnershipIdentityToShow) {
        ctx.addIssue({
          code: 'custom',
          message: 'Can\'t be empty',
          path: ['partnershipIdentityToShow'],
        });
      }

      if (!data.partnershipCreationMode) {
        ctx.addIssue({
          code: 'custom',
          message: 'Can\'t be empty',
          path: ['partnershipCreationMode'],
        });
      }

      if (typeof data.partnershipIsPartnerIdentityFirst !== 'boolean') {
        ctx.addIssue({
          code: 'custom',
          message: 'Can\'t be empty',
          path: ['partnershipIsPartnerIdentityFirst'],
        });
      }

      data.partnershipAdSettings.forEach((setting, index) => {
        if (!isPartnershipIdFilled(setting.partnershipId)) {
          return;
        }

        validateAdSettingsFieldsAtIndex(
          data.partnershipAdSettingsFields[index],
          index,
          ctx,
          'partnershipAdSettingsFields',
        );
      });
    }
  });

export type FacebookFormData = z.infer<typeof schema>;

export default {
  schema,
  resolver: zodResolver(schema),
} as {
  schema: typeof schema;
  resolver: Resolver<z.infer<typeof schema>>;
};
